import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get("token") || "").trim();
  }, []);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token.");
  }, [token]);

  const validate = () => {
    if (!token) return "This reset link is missing its token.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (password !== confirm) return "Passwords do not match.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-lg border border-white/10">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-[#00FF99]">KweryAI</h1>
          <p className="text-gray-400 text-sm mt-1">Set a new password</p>
        </div>

        {done ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-[#00FF99]/40 bg-[#00FF99]/10 px-4 py-3 text-sm text-[#00FF99]">
              Password reset successful. Redirecting you to sign in…
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <label className="block text-xs text-[#00FF99]/70 uppercase tracking-wider mb-1">
              New password
            </label>
            <div className="relative mb-3">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="w-full p-3 pr-11 rounded-xl bg-black/40 border border-white/20 text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50 transition"
                disabled={submitting}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-white/50 hover:text-white"
                tabIndex={-1}
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <label className="block text-xs text-[#00FF99]/70 uppercase tracking-wider mb-1">
              Confirm password
            </label>
            <div className="relative mb-4">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your password"
                className="w-full p-3 pr-11 rounded-xl bg-black/40 border border-white/20 text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50 transition"
                disabled={submitting}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-white/50 hover:text-white"
                tabIndex={-1}
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && (
              <p className="text-red-400 text-sm mb-3" role="alert">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting || !token}
              className="w-full bg-[#00FF99] text-black font-semibold p-3 rounded-xl hover:bg-[#00cc7a] transition disabled:opacity-50"
            >
              {submitting ? "Resetting…" : "Reset password"}
            </button>

            <p className="text-center text-white/40 text-xs mt-5">
              <Link to="/login" className="text-[#00FF99] hover:underline">Back to Sign In</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
